import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, Target, Trophy, AlertCircle, ArrowRight, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/analytics/me");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analytics failed");
    }
    setBusy(false);
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-1">growth analytics</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Your weekly strategy</h1>
          <p className="text-[#64748B] text-sm mt-1">Claude analyzes your posts, products and patrons → plays you can run this week.</p>
        </div>
        <button
          data-testid="rerun-analytics"
          onClick={run}
          disabled={busy}
          className="nexus-btn-primary text-sm flex items-center gap-2"
        >
          <Sparkles size={14} /> {busy ? "Analyzing…" : "Re-analyze"}
        </button>
      </div>

      {!data && (
        <div className="glass rounded-2xl p-10 text-center text-[#64748B]">
          {busy ? "Reading your signal…" : "No analysis yet. Click Re-analyze."}
        </div>
      )}

      {data && (
        <>
          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="glass-heavy rounded-3xl p-6 grain"
            data-testid="analytics-headline"
          >
            <div className="flex items-start gap-3">
              <BarChart3 size={20} className="text-[#00E5FF] mt-1" />
              <p className="text-xl font-display leading-snug">{data.strategy.headline}</p>
            </div>
          </motion.div>

          {/* Scorecard */}
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(data.strategy.scorecard || {}).map(([k, v]) => (
              <ScoreCard key={k} label={k} value={v} />
            ))}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat l="Posts" v={data.summary.post_count} />
            <Stat l="Products" v={data.summary.product_count} />
            <Stat l="Patrons" v={data.summary.active_subscribers} c="#F5C542" />
            <Stat l="Reactions" v={data.summary.total_reactions} c="#FF6B9D" />
            <Stat l="Net $" v={`$${data.summary.lifetime_net_usd.toFixed(0)}`} c="#00E5FF" />
          </div>

          {/* Wins / Gaps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-heavy rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-3"><Trophy size={16} className="text-[#F5C542]" /><div className="font-display font-bold">Wins</div></div>
              <ul className="space-y-2 text-sm" data-testid="analytics-wins">
                {(data.strategy.wins || []).map((w, i) => (
                  <li key={`win-${i}-${w}`} className="flex gap-2 text-[#0F172A]"><span className="text-[#F5C542]">·</span>{w}</li>
                ))}
              </ul>
            </div>
            <div className="glass-heavy rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-3"><AlertCircle size={16} className="text-[#FF6B9D]" /><div className="font-display font-bold">Gaps</div></div>
              <ul className="space-y-2 text-sm" data-testid="analytics-gaps">
                {(data.strategy.gaps || []).map((g, i) => (
                  <li key={`gap-${i}-${g}`} className="flex gap-2 text-[#0F172A]"><span className="text-[#FF6B9D]">·</span>{g}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Plays */}
          <div className="glass-heavy rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-4"><Target size={16} className="text-[#00E5FF]" /><div className="font-display font-bold">Plays for this week</div></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="analytics-plays">
              {(data.strategy.plays || []).map((p, i) => (
                <motion.div
                  key={`play-${i}-${p.title}`}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className="glass rounded-2xl p-4"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5C542] mb-1">play {i + 1}</div>
                  <div className="font-display font-bold mb-2">{p.title}</div>
                  <div className="text-xs text-[#64748B] mb-1"><b className="text-[#7B2CBF]">Why:</b> {p.why}</div>
                  <div className="text-xs text-[#64748B] mb-1"><b className="text-[#00E5FF]">Do:</b> {p.action}</div>
                  <div className="text-xs text-[#F5C542] mt-2 flex items-center gap-1"><TrendingUp size={10} /> {p.expected_lift}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Next post angles */}
          <div className="glass-heavy rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-[#7B2CBF]" /><div className="font-display font-bold">Next post angles</div></div>
            <ul className="space-y-2 text-sm" data-testid="analytics-angles">
              {(data.strategy.next_post_angles || []).map((a, i) => (
                <li key={`angle-${i}-${a}`} className="glass rounded-xl px-3 py-2 flex items-center gap-2"><ArrowRight size={12} className="text-[#00E5FF]" />{a}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function ScoreCard({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="glass-heavy rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{label.replace("_", " ")}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-3xl font-bold text-[#00E5FF]">{v}</div>
        <div className="text-xs text-[#64748B]">/ 100</div>
      </div>
      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${v}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="h-full"
          style={{ background: "linear-gradient(90deg, #00E5FF, #7B2CBF)" }}
        />
      </div>
    </div>
  );
}

function Stat({ l, v, c = "#0F172A" }) {
  return (
    <div className="glass rounded-2xl p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{l}</div>
      <div className="font-display text-2xl font-bold mt-1" style={{ color: c }}>{v}</div>
    </div>
  );
}
